import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@heroicons/react/24/solid';
import { ReactNode } from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  loading?: boolean;
}

export default function MetricCard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  icon,
  trend,
  loading = false,
}: MetricCardProps) {
  const getTrendIcon = () => {
    if (trend === 'up') {
      return <ArrowUpIcon className="w-4 h-4 text-green-500" />;
    }
    if (trend === 'down') {
      return <ArrowDownIcon className="w-4 h-4 text-red-500" />;
    }
    return <MinusIcon className="w-4 h-4 text-gray-400" />;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-4 w-24 bg-gray-200 rounded" />
          <div className="w-10 h-10 bg-gray-200 rounded-lg" />
        </div>
        <div className="h-8 w-32 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-20 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        {icon && (
          <div className="p-2 bg-brand/10 rounded-lg text-brand">{icon}</div>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change !== undefined && trend && (
          <span className={`flex items-center text-sm font-medium ${getTrendColor()}`}>
            {getTrendIcon()}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
      {changeLabel && (
        <p className="mt-1 text-xs text-gray-500">{changeLabel}</p>
      )}
    </div>
  );
}
